/*
  femme Black Friday free gift card
  Version: V3
  Date: 2026-10-19

  Purpose:
  - Pre-launch access is restricted to logged-in customers tagged TEST.
  - Public campaign runs automatically from 16 November 2026 through 30 November 2026.
  - Qualifying merchandise spend excludes all gift-card lines.
  - Exactly one promotion-managed gift card is kept in the cart at the highest qualifying tier.
  - Shopify's automatic Buy X Get Y discounts remain the authority for making that gift card free.
  - Genuine customer-purchased gift cards are never removed or altered by this controller.
*/
(() => {
  'use strict';

  const config = window.FemmeBlackFridayGiftCard;
  if (!config || window.__femmeBlackFridayGiftCardInitialised === true) return;
  window.__femmeBlackFridayGiftCardInitialised = true;

  const PROMO_PROPERTY = '_femme_bf_gift';
  const TIER_PROPERTY = '_femme_bf_tier';
  const VALUE_PROPERTY = '_femme_bf_value';
  const ACTIVE_CLASS = 'femme-bf-gift-active';
  const STATUS_SELECTOR = '[data-femme-bf-gift-status]';
  const rootPath = () => window.Shopify?.routes?.root || '/';

  const tiers = Array.isArray(config.tiers)
    ? config.tiers
        .map((tier) => ({
          threshold: Math.max(0, Number(tier?.threshold) || 0),
          reward: Math.max(0, Number(tier?.reward) || 0),
          variantId: String(tier?.variantId || '')
        }))
        .filter((tier) => tier.threshold > 0 && tier.reward > 0 && tier.variantId)
        .sort((a, b) => a.threshold - b.threshold)
    : [];

  if (!tiers.length) return;

  const publicStart = Date.parse(config.publicStart || '');
  const publicEnd = Date.parse(config.publicEndExclusive || '');

  const campaignState = (now = Date.now()) => {
    if (config.backendReady !== true) return 'inactive';
    if (!Number.isFinite(publicStart) || !Number.isFinite(publicEnd)) return 'inactive';
    if (now >= publicEnd) return 'inactive';
    if (now >= publicStart) return 'public';
    return config.testCustomer === true ? 'test' : 'inactive';
  };

  const isActive = () => campaignState() !== 'inactive';

  const money = (cents) => {
    const value = Math.max(0, Number(cents) || 0) / 100;

    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP',
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (error) {
      return `£${value.toFixed(value % 1 === 0 ? 0 : 2)}`;
    }
  };

  const itemProperties = (item) =>
    item && typeof item.properties === 'object' && item.properties !== null
      ? item.properties
      : {};

  const isPromoLine = (item) => {
    const value = itemProperties(item)[PROMO_PROPERTY];
    return String(value || '').toLowerCase() === 'true';
  };

  const isGiftCardLine = (item) => {
    if (!item) return false;
    if (String(item.handle || '') === String(config.giftCardHandle || '')) return true;

    const variantId = String(item.variant_id || item.id || '');
    return tiers.some((tier) => tier.variantId === variantId);
  };

  const qualifyingSubtotal = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    return items.reduce((total, item) => {
      if (isGiftCardLine(item) || isPromoLine(item)) return total;

      const linePrice = Number(item?.final_line_price);
      return total + (Number.isFinite(linePrice) ? Math.max(0, linePrice) : 0);
    }, 0);
  };

  const tierForSubtotal = (subtotal) => {
    let selected = null;

    tiers.forEach((tier) => {
      if (subtotal >= tier.threshold) selected = tier;
    });

    return selected;
  };

  const nextTier = (currentTier) => {
    if (!currentTier) return tiers[0] || null;

    const index = tiers.findIndex(
      (tier) => tier.threshold === currentTier.threshold && tier.reward === currentTier.reward
    );

    return index >= 0 ? tiers[index + 1] || null : null;
  };

  const messageForCart = (cart) => {
    const subtotal = qualifyingSubtotal(cart);
    const current = tierForSubtotal(subtotal);
    const next = nextTier(current);

    if (!current && next) {
      const left = Math.max(0, next.threshold - subtotal);
      return `Spend ${money(left)} more to unlock a free ${money(next.reward)} gift card.`;
    }

    if (current && next) {
      const left = Math.max(0, next.threshold - subtotal);
      return `You've unlocked a free ${money(current.reward)} gift card. Spend ${money(left)} more and we'll upgrade it to ${money(next.reward)}.`;
    }

    if (current) {
      return `You've unlocked a free ${money(current.reward)} gift card.`;
    }

    return '';
  };

  const fetchCart = async () => {
    const response = await fetch(`${rootPath()}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
    return response.json();
  };

  const postJson = async (path, body) => {
    const response = await fetch(`${rootPath()}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    const result = await response.json();

    if (!response.ok || result?.errors) {
      throw new Error(
        result?.description || result?.message || result?.errors || `Cart request failed (${response.status}).`
      );
    }

    return result;
  };

  const removePromoLines = async (promoLines) => {
    const updates = {};

    promoLines.forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return null;
    return postJson('cart/update.js', { updates });
  };

  const addPromoLine = async (tier) => {
    if (!tier?.variantId) return null;

    return postJson('cart/add.js', {
      items: [
        {
          id: Number(tier.variantId),
          quantity: 1,
          properties: {
            [PROMO_PROPERTY]: 'true',
            [TIER_PROPERTY]: String(tier.threshold / 100),
            [VALUE_PROPERTY]: String(tier.reward / 100)
          }
        }
      ]
    });
  };

  const reconcileThemeCart = async () => {
    if (typeof window.femmeSynchroniseCart !== 'function') return;

    try {
      await window.femmeSynchroniseCart('black-friday-gift-card');
    } catch (error) {
      console.error('[femme BF gift] cart UI reconciliation failed', error);
    }
  };

  let synchronising = null;

  const synchroniseReward = async (initialCart = null) => {
    if (synchronising) return synchronising;

    synchronising = (async () => {
      let cart = initialCart || (await fetchCart());
      let promoLines = (cart.items || []).filter(isPromoLine);
      let changed = false;

      if (!isActive()) {
        if (promoLines.length) {
          await removePromoLines(promoLines);
          changed = true;
          cart = await fetchCart();
        }

        if (changed) await reconcileThemeCart();
        return cart;
      }

      const desired = tierForSubtotal(qualifyingSubtotal(cart));

      if (!desired) {
        if (promoLines.length) {
          await removePromoLines(promoLines);
          changed = true;
          cart = await fetchCart();
        }

        if (changed) await reconcileThemeCart();
        return cart;
      }

      const alreadyCorrect =
        promoLines.length === 1 &&
        String(promoLines[0]?.variant_id || promoLines[0]?.id || '') === desired.variantId &&
        Number(promoLines[0]?.quantity) === 1;

      if (alreadyCorrect) return cart;

      if (promoLines.length) {
        await removePromoLines(promoLines);
        changed = true;
        cart = await fetchCart();
        promoLines = [];
      }

      await addPromoLine(desired);
      changed = true;
      cart = await fetchCart();

      if (changed) await reconcileThemeCart();
      return cart;
    })();

    try {
      return await synchronising;
    } finally {
      synchronising = null;
    }
  };

  const createStatus = () => {
    const element = document.createElement('section');
    element.className = 'femme-bf-gift-card';
    element.setAttribute('data-femme-bf-gift-status', '');
    element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite');
    element.innerHTML = `
      <div class="femme-bf-gift-card__eyebrow">Black Friday gift</div>
      <p class="femme-bf-gift-card__message" data-femme-bf-gift-message></p>
    `;
    return element;
  };

  const ensureStatusIn = (host) => {
    if (!(host instanceof HTMLElement)) return null;

    let status = host.querySelector(`:scope > ${STATUS_SELECTOR}`);
    if (!(status instanceof HTMLElement)) {
      status = createStatus();
      host.prepend(status);
    }

    return status;
  };

  const statusElements = () => {
    const results = new Set();

    document.querySelectorAll('#CartDrawer [id^="CartDrawerBody-"]').forEach((body) => {
      if (!(body instanceof HTMLElement) || body.classList.contains('hidden')) return;
      const status = ensureStatusIn(body);
      if (status) results.add(status);
    });

    document.querySelectorAll('main-cart').forEach((mainCart) => {
      if (!(mainCart instanceof HTMLElement)) return;
      const status = ensureStatusIn(mainCart);
      if (status) results.add(status);
    });

    return [...results];
  };

  const applyPromotionVisibility = () => {
    const active = isActive();
    document.documentElement.classList.toggle(ACTIVE_CLASS, active);

    if (!active) {
      document.querySelectorAll(STATUS_SELECTOR).forEach((element) => element.remove());
    }

    return active;
  };

  const render = (cart) => {
    if (!applyPromotionVisibility()) return;

    const message = messageForCart(cart);

    statusElements().forEach((status) => {
      const target = status.querySelector('[data-femme-bf-gift-message]');
      if (target) target.textContent = message;
      status.hidden = !message;
    });
  };

  let refreshTimer = null;
  let refreshInFlight = null;

  const refresh = async () => {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
      let cart = await fetchCart();
      cart = await synchroniseReward(cart);
      render(cart);
      return cart;
    })();

    try {
      return await refreshInFlight;
    } catch (error) {
      console.error('[femme BF gift]', error);
      return null;
    } finally {
      refreshInFlight = null;
    }
  };

  const scheduleRefresh = (delay = 80) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh(), delay);
  };

  const subscribeToThemeCart = () => {
    const pubsub = window.FoxTheme?.pubsub;
    const eventName = pubsub?.PUB_SUB_EVENTS?.cartUpdate;

    if (!pubsub || typeof pubsub.subscribe !== 'function' || !eventName) return;

    try {
      pubsub.subscribe(eventName, () => scheduleRefresh(40));
    } catch (error) {
      console.error('[femme BF gift] cart subscription failed', error);
    }
  };

  document.addEventListener('cart:updated', () => scheduleRefresh(40));
  document.addEventListener('cart:refresh', () => scheduleRefresh(40));
  window.addEventListener('pageshow', () => scheduleRefresh(0));

  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      if (mutation.type !== 'childList') return false;

      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
        if (!(node instanceof Element)) return false;

        return Boolean(
          node.matches?.('#CartDrawer, main-cart, cart-items, .cart-item') ||
          node.querySelector?.('#CartDrawer, main-cart, cart-items, .cart-item')
        );
      });
    });

    if (relevant) scheduleRefresh(100);
  });

  const scheduleCampaignBoundaryCheck = () => {
    const state = campaignState();
    const now = Date.now();
    const boundary = state === 'public' ? publicEnd : publicStart;

    if (!Number.isFinite(boundary) || boundary <= now) return;

    const MAX_DELAY = 6 * 60 * 60 * 1000;
    const delay = Math.min(Math.max(1000, boundary - now + 250), MAX_DELAY);

    window.setTimeout(() => {
      scheduleRefresh(0);
      scheduleCampaignBoundaryCheck();
    }, delay);
  };

  const initialise = () => {
    applyPromotionVisibility();
    subscribeToThemeCart();

    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    refresh();
    scheduleCampaignBoundaryCheck();
  };

  initialise();
})();
